<script setup lang="ts">
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import TosterCenter from '@/shared/components/core/TosterCenter/TosterCenter.vue';
import { useAppConfig } from '@/shared/composables/useAppConfig.ts';
import {
  Ref, ref, onMounted, onBeforeUnmount,
} from 'vue';
import { MaybeNull } from '@/shared/types';
import { useMainStore } from '@/core/store/main.ts';
import { storeToRefs } from 'pinia';

const deviceFrame$: Ref<MaybeNull<HTMLElement>> = ref(null);

const { desktopSlideContainerHeightMarginPx } = useAppConfig();

const {
  desktopSlideContainerWidth,
  desktopSlideContainerHeight,
  windowWidth,
} = storeToRefs(useMainStore());

let deviceFrameResizeObserver: MaybeNull<ResizeObserver> = null;

const updateWidth = () => {
  if (!deviceFrame$.value) {
    return;
  }

  desktopSlideContainerWidth.value = deviceFrame$.value.offsetWidth;
  desktopSlideContainerHeight.value = deviceFrame$.value.offsetHeight;
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener('resize', updateWidth);

  if (deviceFrame$.value) {
    // On iOS the WKWebView's viewport/safe-area metrics aren't settled yet at
    // mount time, so a one-shot offsetHeight read here can come back as 0.
    // ResizeObserver re-fires once the browser finalizes the real box size.
    deviceFrameResizeObserver = new ResizeObserver(updateWidth);
    deviceFrameResizeObserver.observe(deviceFrame$.value);
  }

  updateWidth();
});

onBeforeUnmount(() => {
  deviceFrameResizeObserver?.disconnect();
});

</script>

<template>
  <div class="main-wrapper">
    <TosterCenter />

    <div
      ref="deviceFrame$"
      class="device-frame"
    >
      <IonApp>
        <IonRouterOutlet />
      </IonApp>
    </div>
  </div>
</template>

<style scoped>
.main-wrapper {
  position: relative;
  height: 100%;
}

.device-frame {
  position: relative;
  flex-direction: column;
  height: 100dvh;
  z-index: 2;
  border-radius: 30px;
}

@media all and (min-width: 601px) {
  .device-frame {
    overflow: hidden;

    width: auto;
    height: calc((100vh - (2 * v-bind('desktopSlideContainerHeightMarginPx'))));
    margin: auto;
    margin-top: v-bind('desktopSlideContainerHeightMarginPx');
    margin-bottom: v-bind('desktopSlideContainerHeightMarginPx');
    max-width: calc(100vw - 200px);

    aspect-ratio: 9 / 16;
  }

  .ion-page {
    zoom: var(--zoom-for-desktop);
  }
}
</style>
